// @ts-nocheck
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SubscriptionDialog } from '@/components/ui/subscription-dialog';
import {
  Loader2, Plane, Clock, Moon, Calendar,
  Activity, BarChart3, Star, TrendingUp, AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useSubscriptionGuard } from '@/hooks/useSubscriptionGuard';
import { fsopApi, type Statistics, type SyncStatus, type FlightLog } from '@/services/api';
import type { LandingCurrencyResult } from '@/types/api.types';
import { cn } from '@/utils/cn';

export default function FsopDashboardPage() {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [recentLogs, setRecentLogs] = useState<FlightLog[]>([]);
  const [landingCurrency, setLandingCurrency] = useState<LandingCurrencyResult | null>(null);
  const [loading, setLoading] = useState(true);
  const { showDialog, setShowDialog } = useSubscriptionGuard();

  const fetchAll = useCallback(async () => {
    if (!token) return;
    try {
      const [statsRes, syncRes, recentRes, currencyRes] = await Promise.all([
        fsopApi.getStatistics(),
        fsopApi.getSyncStatus(),
        fsopApi.getRecentLogs(10),
        fsopApi.getLandingCurrency(),
      ]);
      setStatistics(statsRes.data?.data ?? statsRes.data);
      setSyncStatus(syncRes.data?.data ?? syncRes.data);
      setRecentLogs(recentRes.data?.data ?? recentRes.data ?? []);
      setLandingCurrency(currencyRes.data?.data ?? currencyRes.data);
    } catch (err: any) {
      console.error('Dashboard fetch error:', err);
      if (err.response?.status === 402) {
        setShowDialog(true);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const recordTypeEntries = statistics
    ? Object.entries(statistics.recordTypes || {}).sort((a, b) => b[1] - a[1])
    : [];
  const maxTypeCount = recordTypeEntries.length > 0 ? recordTypeEntries[0][1] : 1;

  // 起落有效期状态颜色
  const getCurrencyStatusColor = (days: number | undefined) => {
    if (days === undefined || days === -9999) return 'text-gray-600 bg-gray-50';
    if (days < 0) return 'text-red-600 bg-red-50';
    if (days <= 7) return 'text-amber-600 bg-amber-50';
    if (days <= 30) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const getCurrencyTextColor = (days: number | undefined) => {
    if (days === undefined || days === -9999) return 'text-gray-600';
    if (days < 0) return 'text-red-600';
    if (days <= 7) return 'text-amber-600';
    if (days <= 30) return 'text-yellow-600';
    return 'text-green-600';
  };

  const formatDaysRemaining = (days: number | undefined) => {
    if (days === undefined) return '--';
    if (days === -9999) return '无记录';
    if (days < 0) return `超期 ${Math.abs(days)} 天`;
    return `${days} 天`;
  };

  return (
    <div className="container mx-auto py-6">
      <SubscriptionDialog
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
      />
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">FSOP 数据仪表盘</h1>
            <p className="text-sm text-gray-500">您的飞行记录数据概览</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate('/fsop/flightlogs')} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            查看飞行记录
          </button>
          <button onClick={() => navigate('/sync')} className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            同步数据
          </button>
        </div>
      </div>

      {!syncStatus?.hasCredential && (
        <div className="mb-6 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm flex items-center gap-2">
          <Star className="w-4 h-4" />
          尚未配置 FSOP 凭据，请在「凭据管理」中添加后再同步数据。
          <button onClick={() => navigate('/sync')} className="ml-auto underline font-medium hover:no-underline">
            前往添加 →
          </button>
        </div>
      )}

      {syncStatus?.isSyncing && (
        <div className="mb-6 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm flex items-center gap-2">
          <Activity className="w-4 h-4" />
          同步任务正在进行中...
          <button onClick={() => navigate('/sync')} className="ml-auto underline font-medium hover:no-underline">
            查看进度 →
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">飞行记录总数</CardTitle>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-50 text-blue-600">
              <Plane className="w-4 h-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics?.totalRecords ?? 0}</div>
            <p className="text-xs text-gray-400 mt-1">条</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">总飞行时间</CardTitle>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-green-50 text-green-600">
              <Clock className="w-4 h-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics?.totalFlightHours ?? 0}</div>
            <p className="text-xs text-gray-400 mt-1">飞行小时</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">总夜间时间</CardTitle>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-amber-50 text-amber-600">
              <Moon className="w-4 h-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics?.totalNightHours ?? 0}</div>
            <p className="text-xs text-gray-400 mt-1">夜间小时</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">近30天记录</CardTitle>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-purple-50 text-purple-600">
              <Calendar className="w-4 h-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics?.recentRecords ?? 0}</div>
            <p className="text-xs text-gray-400 mt-1">条</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" title="点击刷新数据">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">起落有效期</CardTitle>
            <div className={cn(
              "w-8 h-8 rounded-lg flex items-center justify-center",
              getCurrencyStatusColor(landingCurrency?.current.daysRemaining)
            )}>
              <TrendingUp className="w-4 h-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className={cn("text-2xl font-bold", getCurrencyTextColor(landingCurrency?.current.daysRemaining))}>
              {formatDaysRemaining(landingCurrency?.current.daysRemaining)}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {landingCurrency?.current.expiryDate 
                ? `截止: ${landingCurrency.current.expiryDate}` 
                : '无有效记录'}
            </p>
            {landingCurrency && landingCurrency.current.daysRemaining !== -9999 && (
              <p className="text-xs text-green-600 mt-1">
                刷新1个可延 +{landingCurrency.refresh1Landing.daysGain} 天
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 起落有效期详细信息 */}
      {landingCurrency && landingCurrency.current.daysRemaining !== -9999 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Plane className="w-5 h-5 text-indigo-500" />
            <h2 className="text-lg font-semibold text-gray-900">90天3个起落详情</h2>
          </div>
          
          {/* 当前状态卡片 */}
          <Card className="mb-4">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-500" />
                <CardTitle className="text-base">当前有效期状态</CardTitle>
              </div>
              <div className={cn(
                "px-3 py-1 rounded-full text-sm font-medium",
                getCurrencyStatusColor(landingCurrency.current.daysRemaining)
              )}>
                {landingCurrency.current.daysRemaining < 0 ? '已超期' : `剩余 ${landingCurrency.current.daysRemaining} 天`}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">临界日期（第3个）</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {landingCurrency.current.thirdLandingDate || '-'}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">有效期截止日</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {landingCurrency.current.expiryDate || '-'}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">剩余天数</p>
                  <p className={cn("text-xl font-bold", getCurrencyTextColor(landingCurrency.current.daysRemaining))}>
                    {landingCurrency.current.daysRemaining}
                  </p>
                </div>
              </div>

              {/* 最近起落日期 */}
              {landingCurrency.current.landingDates && landingCurrency.current.landingDates.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2">最近起落日期（降序）</p>
                  <div className="flex flex-wrap gap-2">
                    {landingCurrency.current.landingDates.slice(0, 5).map((date, index) => (
                      <span
                        key={`${date}-${index}`}
                        className={cn(
                          "px-2 py-1 rounded-full text-xs",
                          index < 3 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                        )}
                      >
                        {date} {index < 3 && '(有效)'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* 最近3个起落详情 */}
          {landingCurrency.recentLandings && landingCurrency.recentLandings.length > 0 && (
            <Card className="mb-4">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Plane className="w-5 h-5 text-indigo-500" />
                  <CardTitle className="text-base">最近3个起落详情</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {landingCurrency.recentLandings.map((landing, index) => (
                    <div
                      key={index}
                      className={cn(
                        "p-3 rounded-lg border-2",
                        index === 0
                          ? 'border-green-200 bg-green-50'
                          : index === 1
                          ? 'border-blue-200 bg-blue-50'
                          : 'border-purple-200 bg-purple-50'
                      )}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={cn(
                          "text-xs font-medium",
                          index === 0
                            ? 'text-green-700'
                            : index === 1
                            ? 'text-blue-700'
                            : 'text-purple-700'
                        )}>
                          第{index + 1}个起落
                        </span>
                        <span className={cn(
                          "px-2 py-0.5 rounded-full text-xs font-medium",
                          landing.type === 'simulator'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-gray-100 text-gray-700'
                        )}>
                          {landing.type === 'simulator' ? '模拟机' : '日常飞行'}
                        </span>
                      </div>
                      <p className={cn(
                        "text-sm font-bold",
                        index === 0
                          ? 'text-green-900'
                          : index === 1
                          ? 'text-blue-900'
                          : 'text-purple-900'
                      )}>
                        {landing.date}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 p-2 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600">
                    <strong>说明：</strong>第3个起落日期 + 90天 = 有效期截止日
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* 刷新预测卡片 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 刷新1个起落 */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-500" />
                  <CardTitle className="text-sm">刷新1个起落</CardTitle>
                </div>
                <div className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                  +{landingCurrency.refresh1Landing.daysGain} 天
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-xs">场景</span>
                    <span className="font-medium text-xs">{landingCurrency.refresh1Landing.scenario}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-xs">新的有效期截止日</span>
                    <span className="font-medium text-green-600 text-xs">{landingCurrency.refresh1Landing.newExpiryDate || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-xs">新的剩余天数</span>
                    <span className={cn("font-bold text-xs", landingCurrency.refresh1Landing.newDaysRemaining < 0 ? 'text-red-600' : 'text-green-600')}>
                      {landingCurrency.refresh1Landing.newDaysRemaining} 天
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-1 border-t">
                    <span className="text-gray-500 text-xs">有效期延长</span>
                    <span className="font-bold text-green-600 text-xs">+{landingCurrency.refresh1Landing.daysGain} 天</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 刷新2个起落 */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-500" />
                  <CardTitle className="text-sm">刷新2个起落</CardTitle>
                </div>
                <div className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                  +{landingCurrency.refresh2Landings.daysGain} 天
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-xs">场景</span>
                    <span className="font-medium text-xs">{landingCurrency.refresh2Landings.scenario}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-xs">新的有效期截止日</span>
                    <span className="font-medium text-blue-600 text-xs">{landingCurrency.refresh2Landings.newExpiryDate || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-xs">新的剩余天数</span>
                    <span className={cn("font-bold text-xs", landingCurrency.refresh2Landings.newDaysRemaining < 0 ? 'text-red-600' : 'text-blue-600')}>
                      {landingCurrency.refresh2Landings.newDaysRemaining} 天
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-1 border-t">
                    <span className="text-gray-500 text-xs">有效期延长</span>
                    <span className="font-bold text-blue-600 text-xs">+{landingCurrency.refresh2Landings.daysGain} 天</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">记录类型分布</CardTitle>
            <CardDescription>各类型飞行记录数量</CardDescription>
          </CardHeader>
          <CardContent>
            {recordTypeEntries.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">暂无数据</p>
            ) : (
              <div className="space-y-3">
                {recordTypeEntries.map(([type, count]) => (
                  <div key={type}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600 truncate">{type || '(无类型)'}</span>
                      <span className="font-medium text-gray-900">{count}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all"
                        style={{ width: `${(count / maxTypeCount) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base">最近飞行记录</CardTitle>
              <CardDescription>最新 10 条记录</CardDescription>
            </div>
            <button onClick={() => navigate('/fsop/flightlogs')} className="text-sm text-blue-600 hover:underline">
              查看全部 →
            </button>
          </CardHeader>
          <CardContent>
            {recentLogs.length === 0 ? (
              <div className="text-center py-8">
                <Plane className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-400">暂无飞行记录</p>
                <button onClick={() => navigate('/sync')} className="mt-3 text-sm text-blue-600 hover:underline">
                  立即同步 →
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-400 border-b">
                      <th className="text-left pb-2 font-medium">日期</th>
                      <th className="text-left pb-2 font-medium">航班号</th>
                      <th className="text-left pb-2 font-medium">航线</th>
                      <th className="text-right pb-2 font-medium">飞行时间</th>
                      <th className="text-left pb-2 font-medium">类型</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {recentLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="py-2 text-gray-600">{log.airDate || '-'}</td>
                        <td className="py-2 font-medium">{log.airNum || '-'}</td>
                        <td className="py-2 text-gray-600">{log.departure || '-'} → {log.arrival || '-'}</td>
                        <td className="py-2 text-right text-gray-600">
                          {log.flightTime ? `${Math.floor(log.flightTime / 60)}h${log.flightTime % 60}m` : '-'}
                        </td>
                        <td className="py-2">
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                            {log.recordType || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
